import { Accordion as AccordionPrimitive } from "radix-ui";
import { ChevronDownIcon } from "lucide-react";
import React, { useMemo } from "react";

import { cn } from "../../utils";
import { groupSkillFrontmatterEntries, parseSkillFrontmatterEntries } from "../skill-frontmatter";

import { FrontmatterReadView } from "./FrontmatterReadView";

export interface FrontmatterPanelProps {
  frontmatter: string | null;
}

const Accordion = AccordionPrimitive.Root;

const AccordionItem = ({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>): React.JSX.Element => (
  <AccordionPrimitive.Item className={cn("border-b", className)} {...props} />
);

const AccordionTrigger = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>): React.JSX.Element => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      className={cn(
        "flex flex-1 items-center justify-between py-4 text-sm font-medium transition-all",
        "hover:underline [&[data-state=open]>svg]:rotate-180",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronDownIcon className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
);

const AccordionContent = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>): React.JSX.Element => (
  <AccordionPrimitive.Content
    className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn("pb-4 pt-0", className)}>{children}</div>
  </AccordionPrimitive.Content>
);

export const FrontmatterPanel = ({ frontmatter }: FrontmatterPanelProps) => {
  const entries = useMemo(() => parseSkillFrontmatterEntries(frontmatter), [frontmatter]);
  const grouped = useMemo(() => groupSkillFrontmatterEntries(entries), [entries]);

  if (!frontmatter?.trim()) {
    return null;
  }

  return (
    <div className="border-b border-gray-200 bg-gray-50/60 px-2 py-1.5">
      <Accordion type="single" collapsible defaultValue="frontmatter">
        <AccordionItem value="frontmatter" className="overflow-hidden border-0 bg-transparent">
          <div className="flex items-center justify-between gap-2 px-3">
            <AccordionTrigger className="px-0 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 hover:no-underline">
              Frontmatter
            </AccordionTrigger>
          </div>
          <AccordionContent className="p-0">
            {entries.length > 0 ? (
              <FrontmatterReadView
                topLevelEntries={grouped.topLevelEntries}
                groups={grouped.groups}
              />
            ) : (
              <div className="px-3 pb-2 text-xs text-gray-500">
                This frontmatter contains YAML that is not shown as key/value fields.
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
